// M16B-07D-B5-FC — Aftercare Dashboard, canonical projection over
// `patient_case_plans` via `/api/clinic/planner/my-tasks`.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  HeartPulse, AlertTriangle, CheckCircle, Clock, RefreshCw,
} from 'lucide-react';
import { api } from '../../utils/api';

// Canonical row status → next transition. Keys are backend enum values
// (unchanged); display labels come from i18n.
const ROW_NEXT_STATUSES = {
  pending: ['done'],
  assigned: ['done'],
  in_progress: ['done'],
  scheduled: ['done'],
  blocked: ['done'],
  done: ['pending'],
};

// Visual class per canonical status enum. Backend values remain the SoT.
const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  assigned: 'bg-amber-100 text-amber-800 border-amber-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  scheduled: 'bg-blue-100 text-blue-800 border-blue-300',
  blocked: 'bg-red-100 text-red-800 border-red-300',
  done: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

const ACTIVE_STATUSES = new Set(['pending', 'assigned', 'in_progress', 'scheduled']);
const BLOCKED_STATUSES = new Set(['blocked']);
const DONE_STATUSES = new Set(['done']);

const AfterSalesDashboard = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [refreshTick, setRefreshTick] = useState(0);

  const statusLabel = (s) => t(`provider.afterSales.statuses.${s}`, { defaultValue: s });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const { data } = await api.get('/clinic/planner/my-tasks?scope=mine');
        const all = Array.isArray(data?.tasks) ? data.tasks : [];
        if (!cancelled) setTasks(all.filter(x => (x.assigned_role || '') === 'After Sales'));
      } catch (e) {
        console.error('[AfterSalesDashboard] fetch failed', e);
        if (!cancelled) setTasks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const refresh = useCallback(() => setRefreshTick(n => n + 1), []);

  const transition = useCallback(async (task, target) => {
    if (!task?._case_id || !task?.row_id) return;
    const resolved = target === 'done';
    try {
      await api.post(`/clinic/cases/${task._case_id}/plan/overrides`, {
        op: 'resolve_row',
        args: { row_id: task.row_id, resolved },
      });
      refresh();
    } catch (e) {
      console.error('[AfterSalesDashboard] resolve failed', e);
      alert(t('provider.afterSales.updateFailed', { message: e?.response?.data?.message || e.message }));
    }
  }, [refresh, t]);

  const stats = useMemo(() => ({
    active:    tasks.filter(x => ACTIVE_STATUSES.has((x.status || '').toLowerCase())).length,
    escalated: tasks.filter(x => BLOCKED_STATUSES.has((x.status || '').toLowerCase())).length,
    overdue:   tasks.filter(x => x.overdue === true).length,
    completed: tasks.filter(x => DONE_STATUSES.has((x.status || '').toLowerCase())).length,
  }), [tasks]);

  const visible = useMemo(() => tasks.filter(x => {
    const s = (x.status || '').toLowerCase();
    if (activeTab === 'active')    return ACTIVE_STATUSES.has(s);
    if (activeTab === 'escalated') return BLOCKED_STATUSES.has(s);
    if (activeTab === 'completed') return DONE_STATUSES.has(s);
    return true;
  }), [tasks, activeTab]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="after-sales-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3" data-testid="aftercare-dashboard-title">
              <HeartPulse className="h-7 w-7 text-emerald-600" />
              {t('provider.afterSales.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-testid="aftercare-dashboard-subtitle">
              {t('provider.afterSales.subtitle')}
            </p>
          </div>
          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="refresh-dashboard-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> {t('provider.afterSales.refresh')}
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-emerald-500" data-testid="stat-active">
            <CardContent className="py-4 px-4 flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-emerald-700">{stats.active}</p>
                <p className="text-xs text-emerald-600">{t('provider.afterSales.stats.active')}</p>
              </div>
              <HeartPulse className="h-8 w-8 text-emerald-200" />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500" data-testid="stat-escalated">
            <CardContent className="py-4 px-4 flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.escalated}</p>
                <p className="text-xs text-red-600">{t('provider.afterSales.stats.escalated')}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-200" />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500" data-testid="stat-overdue">
            <CardContent className="py-4 px-4 flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.overdue}</p>
                <p className="text-xs text-amber-600">{t('provider.afterSales.stats.overdue')}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-200" />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500" data-testid="stat-completed">
            <CardContent className="py-4 px-4 flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.completed}</p>
                <p className="text-xs text-blue-600">{t('provider.afterSales.stats.completed')}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-200" />
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="active"    data-testid="tab-active">    <HeartPulse className="h-4 w-4 mr-2" /> {t('provider.afterSales.tabs.active')} ({stats.active})</TabsTrigger>
            <TabsTrigger value="escalated" data-testid="tab-escalated"> <AlertTriangle className="h-4 w-4 mr-2" /> {t('provider.afterSales.tabs.escalated')} ({stats.escalated})</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed"> <CheckCircle className="h-4 w-4 mr-2" /> {t('provider.afterSales.tabs.completed')} ({stats.completed})</TabsTrigger>
            <TabsTrigger value="all"       data-testid="tab-all">{t('provider.afterSales.tabs.all')} ({tasks.length})</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12" data-testid="aftercare-loading">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : visible.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border" data-testid="no-aftercare-tasks">
                <HeartPulse className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">{t('provider.afterSales.empty')}</p>
                <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto" data-testid="no-aftercare-tasks-hint">{t('provider.afterSales.emptyHint')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {visible.map(task => {
                  const status = task.status || 'pending';
                  const nexts = ROW_NEXT_STATUSES[status] || [];
                  return (
                    <Card
                      key={`${task._case_id}::${task.row_id}`}
                      className="border-l-4"
                      style={{ borderLeftColor: status === 'done' ? '#3b82f6' : status === 'blocked' ? '#ef4444' : '#10b981' }}
                      data-testid={`aftercare-task-${task.row_id}`}
                    >
                      <CardContent className="py-3 px-4 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-gray-900 truncate" data-testid={`aftercare-task-${task.row_id}-title`}>
                              {task.title || t('provider.afterSales.task.untitled')}
                            </p>
                            <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[status] || ''}`} data-testid={`aftercare-task-${task.row_id}-status`}>
                              {statusLabel(status)}
                            </Badge>
                            {task.journey_step && (
                              <Badge variant="outline" className="text-[10px]">{task.journey_step}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1" data-testid={`aftercare-task-${task.row_id}-meta`}>
                            {t('provider.afterSales.task.case')} <code className="text-[11px]">{(task._case_id || '').slice(0, 8)}</code> · {t('provider.afterSales.task.role')} {task.assigned_role || '—'} · {task._treatment || t('provider.afterSales.task.treatmentUnknown')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {nexts.length === 0 ? (
                            <span className="text-xs text-gray-400">{t('provider.afterSales.task.noAction')}</span>
                          ) : (
                            <select
                              value=""
                              onChange={(e) => { const v = e.target.value; if (v) transition(task, v); }}
                              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                              data-testid={`aftercare-task-${task.row_id}-status-picker`}
                            >
                              <option value="">{t('provider.afterSales.task.setStatus')}</option>
                              {nexts.map(n => <option key={n} value={n}>{statusLabel(n)}</option>)}
                            </select>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <p className="text-[11px] text-gray-400 pt-2" data-testid="aftercare-dashboard-projection-mode">
          {t('provider.afterSales.projectionNote')}
        </p>
      </div>
    </div>
  );
};

export default AfterSalesDashboard;
